import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { MouseEventArgs } from "../core/events.js";
import { clamp, type Point, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { GraphNodeVisual, Theme } from "../themes/Theme.js";

/** What a node can be, from the player's side. */
export type NodeState =
  /** Not reachable yet: a prerequisite is missing. */
  | "locked"
  /** Reachable and affordable. */
  | "available"
  /** Partly taken — a rank 2 of 5. */
  | "partial"
  /** Fully taken. */
  | "taken";

export interface GraphNode {
  id: string;
  label: string;
  /** Position in graph space, the center of the node. */
  x: number;
  y: number;
  /** Ids of the nodes that must be taken first. */
  requires?: readonly string[];
  /** Ranks taken, and how many there are. */
  rank?: number;
  maxRank?: number;
  glyph?: string;
  color?: Color | null;
  /** Size override; the graph's `nodeSize` otherwise. */
  size?: number;
  data?: unknown;
}

export interface NodeGraphOptions extends ControlOptions {
  nodes?: readonly GraphNode[];
  /** Default node diameter, in graph units. */
  nodeSize?: number;
  /** Thickness of the lines between nodes. */
  edgeWidth?: number;
  font?: FontSpec | null;
  /** Draw the rank pips round the node's edge. */
  showRanks?: boolean;
  onNodeClick?: (node: GraphNode) => void;
}

const DEFAULT_NODE_SIZE = 48;
const DEFAULT_EDGE_WIDTH = 2;
const PIP_SIZE = 3;

/**
 * A skill tree, tech tree or talent web: nodes joined by prerequisite lines.
 *
 * Meant to be dropped inside a `ScrollViewer`, which already pans and pinches
 * and zooms from 0.25x to 4x — a tech tree is a large canvas viewed through a
 * small window, and that is exactly what a scroll viewer is for. Nothing here
 * reimplements any of it.
 *
 * The graph draws edges before nodes so a line never crosses a face, and
 * colors each edge by whether its prerequisite has actually been met, which
 * is how a player reads a route through the tree at a glance.
 */
export class NodeGraph extends Control {
  #nodes: GraphNode[];
  #byId = new Map<string, GraphNode>();
  #hovered: string | null = null;

  nodeSize: number;
  edgeWidth: number;
  font: FontSpec | null;
  showRanks: boolean;

  constructor(options: NodeGraphOptions = {}) {
    super({ width: 600, height: 400, tabStop: true, ...options });
    this.nodeSize = options.nodeSize ?? DEFAULT_NODE_SIZE;
    this.edgeWidth = options.edgeWidth ?? DEFAULT_EDGE_WIDTH;
    this.font = options.font ?? null;
    this.showRanks = options.showRanks ?? true;
    this.#nodes = [];
    this.nodes = options.nodes ?? [];
    if (options.onNodeClick) {
      this.on("itemactivate", (index) => {
        const node = this.#nodes[index];
        if (node) options.onNodeClick!(node);
      });
    }

    this.on("pointermove", (e) => this.#hover(e));
    this.on("pointerleave", () => this.#setHovered(null));
    this.on("click", (e) => this.#onClick(e));
  }

  get nodes(): readonly GraphNode[] {
    return this.#nodes;
  }
  set nodes(v: readonly GraphNode[]) {
    this.#nodes = [...v];
    this.#byId = new Map(this.#nodes.map((n) => [n.id, n]));
    this.emit("change");
    this.invalidate();
  }

  find(id: string): GraphNode | null {
    return this.#byId.get(id) ?? null;
  }

  /** Rewrite one node in place. */
  update(id: string, node: GraphNode): boolean {
    const i = this.#nodes.findIndex((n) => n.id === id);
    if (i < 0) return false;
    this.#nodes[i] = node;
    this.#byId.set(node.id, node);
    this.emit("change");
    this.invalidate();
    return true;
  }

  /** True when every prerequisite of `node` is fully taken. */
  isUnlocked(node: GraphNode): boolean {
    return (node.requires ?? []).every((id) => {
      const required = this.#byId.get(id);
      return required ? this.stateOf(required) === "taken" : false;
    });
  }

  /** What state a node is in, from its ranks and its prerequisites. */
  stateOf(node: GraphNode): NodeState {
    const rank = node.rank ?? 0;
    const max = node.maxRank ?? 1;
    if (rank >= max) return "taken";
    if (rank > 0) return "partial";
    return this.isUnlocked(node) ? "available" : "locked";
  }

  /** The node's box in graph coordinates. */
  boundsOf(node: GraphNode): Rect {
    const size = node.size ?? this.nodeSize;
    return { x: node.x - size / 2, y: node.y - size / 2, width: size, height: size };
  }

  /** The node containing a local point, or null. */
  nodeAt(x: number, y: number): GraphNode | null {
    // Backwards: the last drawn is the one on top, and so the one hit.
    for (let i = this.#nodes.length - 1; i >= 0; i--) {
      const node = this.#nodes[i]!;
      const size = node.size ?? this.nodeSize;
      if (Math.hypot(x - node.x, y - node.y) <= size / 2) return node;
    }
    return null;
  }

  /** Size the control to hold every node, with `margin` round the outside. */
  fitToNodes(margin = 40): void {
    if (this.#nodes.length === 0) return;
    let right = 0;
    let bottom = 0;
    for (const node of this.#nodes) {
      const b = this.boundsOf(node);
      right = Math.max(right, b.x + b.width);
      bottom = Math.max(bottom, b.y + b.height);
    }
    this.setSize(Math.ceil(right + margin), Math.ceil(bottom + margin));
  }

  #hover(e: MouseEventArgs): void {
    this.#setHovered(this.nodeAt(e.x, e.y)?.id ?? null);
  }

  #setHovered(id: string | null): void {
    if (id === this.#hovered) return;
    this.#hovered = id;
    this.invalidate();
  }

  #onClick(e: MouseEventArgs): void {
    const node = this.nodeAt(e.x, e.y);
    if (!node) return;
    const index = this.#nodes.indexOf(node);
    this.emit("itemactivate", index);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const font = this.font ?? theme.metrics.font;

    // Edges first, so no line is drawn across a node's face.
    for (const node of this.#nodes) {
      for (const id of node.requires ?? []) {
        const from = this.#byId.get(id);
        if (!from) continue;
        const met = this.stateOf(from) === "taken";
        theme.drawGraphEdge(
          rc,
          { x: from.x, y: from.y },
          { x: node.x, y: node.y },
          this.edgeWidth,
          met,
        );
      }
    }

    for (const node of this.#nodes) {
      const state = this.stateOf(node);
      const box = this.boundsOf(node);
      const center: Point = { x: node.x, y: node.y };
      const radius = box.width / 2;
      const hovered = node.id === this.#hovered;

      const visual: GraphNodeVisual = {
        ...this.visualState(),
        hovered,
        center: center,
        radius,
        locked: state === "locked",
        partial: state === "partial",
        taken: state === "taken",
        color: node.color ?? null,
      };
      theme.drawGraphNode(rc, visual);

      const ink = theme.graphNodeTextColor(visual);
      if (node.glyph) {
        const size = radius;
        paintGlyph(node.glyph, {
          rc,
          box: { x: center.x - size / 2, y: center.y - size / 2, width: size, height: size },
          ink,
          weight: 2,
        });
      }

      // Rank pips round the lower edge: how far into a multi-rank node you are.
      const max = node.maxRank ?? 1;
      if (this.showRanks && max > 1) {
        const rank = clamp(node.rank ?? 0, 0, max);
        const spread = Math.PI * 0.55;
        for (let i = 0; i < max; i++) {
          const t = max > 1 ? i / (max - 1) : 0.5;
          const angle = Math.PI - spread / 2 + spread * t;
          const at: Point = {
            x: center.x + Math.sin(angle) * (radius + 5),
            y: center.y - Math.cos(angle) * (radius + 5),
          };
          theme.drawGraphPip(rc, at, PIP_SIZE, i < rank, node.color ?? null);
        }
      }

      const label = node.label;
      if (!label) continue;
      const width = rc.measureText(label, font).width;
      rc.drawText(
        label,
        center.x - width / 2,
        box.y + box.height + (this.showRanks && max > 1 ? 12 : 4),
        font,
        state === "locked" ? p.textDisabled : p.text,
        { baseline: "top" },
      );
    }
  }

  /** A node graph is discs and gaps; the gaps belong to whatever is behind. */
  protected override hitTestSelf(p: Point): boolean {
    return this.nodeAt(p.x, p.y) !== null;
  }
}
